import { useState } from "react"
import { useStripe } from "@stripe/react-stripe-js"
import { useAccountAspect } from "@oysterhr/identity/AccountAspectContext"
import { useConfiguredToasts, useReusableWizard } from "@oysterhr/feature-components"
import { captureException, captureMessage } from "@sentry/react"
import { useCompanyPaymentsSettingsContext } from "../../../../contexts/CompanyPaymentsSettingsContext"
import useCreateSepaPaymentMethod from "../../SepaFlowPage/hooks/useCreateSepaPaymentMethod"
import useCreateSepaSetupIntent from "../../SepaFlowPage/hooks/useCreateSepaSetupIntent"
import { useCompanyAddress } from "../../../hooks/useCompanyAddress"
import { AddSepaFormValues } from "../../../types"

export const useContractorSepaPaymentMethod = () => {
  const { accountAspect: companyId } = useAccountAspect()
  const stripe = useStripe()
  const { goToNext } = useReusableWizard()
  const { paymentsMandateSetup } = useCompanyPaymentsSettingsContext()
  const [confirmingSepaDirectDebit, setConfirmingSepaDirectDebit] = useState(false)
  const { displayErrorToast } = useConfiguredToasts()

  const [createSepaPaymentMethod, { loading: creatingSepaPaymentMethod }] =
    useCreateSepaPaymentMethod()

  const [createSepaSetupIntent, { loading: loadingIntent }] = useCreateSepaSetupIntent()

  const { data: companyAddressData } = useCompanyAddress({
    variables: { companyId },
  })

  const loading = creatingSepaPaymentMethod || loadingIntent || confirmingSepaDirectDebit

  const createStripeSetupIntent = async () => {
    let clientSecret = ""
    try {
      await createSepaSetupIntent({
        variables: { companyId },
        onCompleted: data => {
          clientSecret = data.createSepaSetupIntent.setupIntent?.clientSecret || ""
          if (data.createSepaSetupIntent.errors.length) {
            displayErrorToast({
              content: data.createSepaSetupIntent.errors.join(", "),
            })
          }
        },
      })
    } catch (err) {
      captureException(err)
    }

    return clientSecret
  }

  const getSepaDebitSetupIntent = async (values: AddSepaFormValues) => {
    if (!stripe) {
      throw new Error("Stripe isn't ready")
    }

    const stripeClientSecret = await createStripeSetupIntent()
    if (!stripeClientSecret) {
      throw new Error("Failed to create SEPA setup intent")
    }

    const company = companyAddressData?.company
    const address = company?.billingAddress || company?.address
    const sepaAddress = {
      addressLine1: values.addressLine1,
      addressLine2: values.addressLine2,
      postalCode: values.postalCode,
      countryCode: values.countryCode,
      region: values.region,
      city: values.city,
    }

    // If mandate setup feature flag is enabled we use the new values
    const billingAddress = paymentsMandateSetup ? sepaAddress : address
    setConfirmingSepaDirectDebit(true)

    const { setupIntent, error } = await stripe.confirmSepaDebitSetup(stripeClientSecret, {
      payment_method: {
        sepa_debit: { iban: values.iban },
        billing_details: {
          name: values.name,
          email: values.email,
          address: {
            line1: billingAddress?.addressLine1,
            line2: billingAddress?.addressLine2,
            postal_code: billingAddress?.postalCode,
            country: billingAddress?.countryCode,
            city: billingAddress?.city,
            state: billingAddress?.region,
          },
        },
      },
    })
    setConfirmingSepaDirectDebit(false)

    if (error) {
      captureMessage(error.message || "Couldn't submit SEPA direct debit details", {
        extra: {
          ...error,
        },
      })
      throw new Error(error.message)
    }

    return setupIntent
  }

  const createContractorPaymentMethod = async (values: AddSepaFormValues) => {
    try {
      const setupIntent = await getSepaDebitSetupIntent(values)

      return await createSepaPaymentMethod({
        variables: { ownerId: companyId, setupIntentId: setupIntent?.id },
        onCompleted: data => {
          if (data.createPaymentMethodStripeSepa.errors.length) {
            displayErrorToast({
              content: data.createPaymentMethodStripeSepa.errors.join(", "),
            })
          } else {
            goToNext()
          }
        },
      })
    } catch (err) {
      displayErrorToast({
        content: (err as Error).message,
      })
      console.error(err)
      throw err
    }
  }

  return {
    createContractorPaymentMethod,
    loading,
    isReady: !!stripe,
  }
}
